/**
 * DemoRequestModal — public "Request a Demo" form.
 * Posts to /api/demo-requests which stores the row in MongoDB and emails
 * andresmocny@heavenlyaisolutions.com via Resend. Form is fully self-contained
 * (no auth needed) and used by the Atrium marketing landing page.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  { value: '', label: 'Select project type' },
  { value: 'new-opening', label: 'New opening / pre-opening' },
  { value: 'rebuild', label: 'Reconstruction / rebuild' },
  { value: 'renovation', label: 'Renovation / refurbishment' },
  { value: 'remodel', label: 'Remodel / repositioning' },
  { value: 'operations', label: 'Ongoing operations management' },
  { value: 'other', label: 'Other' },
];

export default function DemoRequestModal({ open, onClose }) {
  const [form, setForm] = useState({
    name: '', email: '', company: '', phone: '', role: '', project_type: '', message: '',
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setDone(false); setErr(''); setBusy(false);
      setForm({ name: '', email: '', company: '', phone: '', role: '', project_type: '', message: '' });
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { setErr('Name and email are required.'); return; }
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/demo-requests`, form);
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Submission failed. Please email andres@heavenlyaisolutions.com directly.');
    } finally { setBusy(false); }
  };

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(2, 6, 23, 0.85)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
      data-testid="atrium-demo-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl rounded-2xl overflow-hidden relative"
        style={{
          background: 'linear-gradient(180deg, #0B1830 0%, #06101F 100%)',
          border: '1px solid rgba(125, 211, 252, 0.18)',
          boxShadow: '0 24px 80px rgba(56, 189, 248, 0.15)',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
          style={{ background: 'rgba(255,255,255,0.06)', color: '#CBD5E1' }}
          data-testid="atrium-demo-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-7 pt-7 pb-3">
          <p className="text-[11px] font-bold tracking-[0.25em] uppercase" style={{ color: '#7DD3FC', fontFamily: 'Manrope' }}>
            Atrium · Demo Request
          </p>
          <h2 className="mt-2 text-3xl font-medium" style={{ fontFamily: "'Cormorant Garamond', serif", color: 'white' }}>
            Let's run a tour of your property.
          </h2>
          <p className="mt-1.5 text-sm" style={{ color: '#94A3B8', fontFamily: 'Manrope' }}>
            Tell us about your project — Andres Mocny will be in touch personally to walk you through it.
          </p>
        </div>

        {done ? (
          <div className="px-7 pb-7 pt-2" data-testid="atrium-demo-success">
            <div className="rounded-xl p-5 flex items-start gap-3" style={{ background: 'rgba(56, 189, 248, 0.08)', border: '1px solid rgba(56, 189, 248, 0.25)' }}>
              <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: '#7DD3FC' }} />
              <div>
                <p className="font-bold text-white" style={{ fontFamily: 'Manrope' }}>Request received.</p>
                <p className="mt-1 text-sm" style={{ color: '#CBD5E1', fontFamily: 'Manrope' }}>
                  You'll hear from Andres Mocny within 24 hours. In the meantime, you can explore the live Dreams Rose Hall workspace running on Atrium below.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="mt-4 w-full py-3 rounded-full font-bold text-sm transition-all hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #38BDF8 0%, #2563EB 100%)', color: 'white', fontFamily: 'Manrope' }}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-7 pb-7 pt-2 space-y-3" data-testid="atrium-demo-form">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" required testid="demo-name">
                <input type="text" value={form.name} onChange={update('name')} required className={inputCls} />
              </Field>
              <Field label="Email" required testid="demo-email">
                <input type="email" value={form.email} onChange={update('email')} required className={inputCls} />
              </Field>
              <Field label="Company / Property" testid="demo-company">
                <input type="text" value={form.company} onChange={update('company')} className={inputCls} />
              </Field>
              <Field label="Role" testid="demo-role">
                <input type="text" value={form.role} onChange={update('role')} className={inputCls} placeholder="GM, Owner, Director…" />
              </Field>
              <Field label="Phone" testid="demo-phone">
                <input type="tel" value={form.phone} onChange={update('phone')} className={inputCls} />
              </Field>
              <Field label="Project type" testid="demo-project-type">
                <select value={form.project_type} onChange={update('project_type')} className={inputCls}>
                  {PROJECT_TYPES.map((p) => <option key={p.value} value={p.value} style={{ background: '#0B1830' }}>{p.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label="What would you like to manage in Atrium?" testid="demo-message">
              <textarea rows={3} value={form.message} onChange={update('message')} className={inputCls + ' resize-none'} />
            </Field>
            {err && <p className="text-xs" style={{ color: '#FCA5A5' }}>{err}</p>}
            <button
              type="submit"
              disabled={busy}
              data-testid="atrium-demo-submit"
              className="w-full py-3 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #38BDF8 0%, #2563EB 100%)', color: 'white', fontFamily: 'Manrope', boxShadow: '0 8px 32px rgba(56,189,248,0.35)' }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {busy ? 'Sending…' : 'Send request'}
            </button>
            <p className="text-[10px] text-center" style={{ color: '#64748B', fontFamily: 'Manrope' }}>
              We'll only use your details to schedule the demo. Powered by Heavenly AI Solutions.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 rounded-lg text-sm outline-none transition-colors';

function Field({ label, required, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#7DD3FC', fontFamily: 'Manrope' }}>
        {label} {required && <span style={{ color: '#FCA5A5' }}>*</span>}
      </span>
      {/* Styled inputs via CSS-in-JS wrapper — react-style injection of dynamic style block keeps
          the dark-theme inputs from inheriting global form styling. */}
      <div style={{ '--bg': 'rgba(255,255,255,0.05)', '--border': 'rgba(125,211,252,0.2)' }}>
        {children}
      </div>
      <style>{`
        [data-testid="${testid}"] input,
        [data-testid="${testid}"] select,
        [data-testid="${testid}"] textarea {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(125,211,252,0.2);
          color: white;
          font-family: 'Manrope', sans-serif;
        }
        [data-testid="${testid}"] input:focus,
        [data-testid="${testid}"] select:focus,
        [data-testid="${testid}"] textarea:focus {
          border-color: #7DD3FC;
          box-shadow: 0 0 0 3px rgba(56,189,248,0.15);
        }
      `}</style>
    </label>
  );
}
